import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { ApplicationState } from '../../store';
import { SharedMaterialCoreData, SharedMaterialAccessType, localizeSharedMaterialFmdDisclosureType, SharedMaterialsCatalogCompany } from '../../store/Models/SharedMaterials/DataModel';
import * as BrowserStateModels from "../../store/Models/SharedMaterials/BrowserStateModels";
import * as SharedMaterialsDatabaseStateController from '../../store/StateControllers/SharedMaterials/SharedMaterialsDatabaseStateController';
import { SharedMaterialsReactUtilities } from './SharedMaterialsReactUtilities';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import { ToxnotComponent, ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import '../../utilities/Utilities'
import './SharedMaterialsBrowser.css';
import { FilterId } from '../../store/Models/Filter';
import { CollapsibleContainer } from '../core/CollapsibleContainer';
import { TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import { SharedMaterialDataModelType, rowPerPageValue } from '../../store/StateControllers/SharedMaterials/SharedMaterialsDatabaseStateController';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';

// ===============================================================================

enum SortDirectionType {
    Ascending,
    Descending
}

interface OwnProps {
    browserEndpoint: SharedMaterialsDatabaseStateController.BrowserEndpoint;
    companyId: string | null | undefined;
}

// At runtime, Redux will merge together...
type SharedMaterialsHomeProps =
    SharedMaterialsDatabaseStateController.SharedMaterialsState &
    SharedMaterialsDatabaseStateController.BrowserState & // ... state we've requested from the Redux store
    ToxnotComponentProperties &
    typeof SharedMaterialsDatabaseStateController.actionCreators & // ... plus action creators we've requested
    OwnProps & // ... plus props from jsx
    RouteComponentProps; // route props like history

// ===============================================================================

interface SharedMaterialsBrowserState {
    filterMenuIdsToTruncateFilterMenu: { [filterMenuId: string]: boolean },
    selectedFilters: { [filterId: string]: string[] };
    sortType: BrowserStateModels.SortTypes,
    sortDirection: SortDirectionType,
    searchValue: string
}

// ===============================================================================

class SharedMaterialsBrowser extends ToxnotComponent<SharedMaterialsHomeProps, SharedMaterialsBrowserState> {

    // ===============================================================================

    constructor(props: SharedMaterialsHomeProps) {
        super(props);

        this.state = {
            filterMenuIdsToTruncateFilterMenu: this.createfilterMenuIdsToTruncateFilterMenu(),
            selectedFilters: {},
            sortType: BrowserStateModels.SortTypes.PassportName,
            sortDirection: SortDirectionType.Ascending,
            searchValue: ""
        }
    }

    // ===============================================================================

    private readonly _filterMenuDefaultOptionTruncateVal: number = 4;
    private readonly _RowPerPageValue: number = rowPerPageValue;
    private readonly legacyComplianceFilters = [
        'California Proposition 65',  //score_cal65
        'Conflict Minerals',  // score_cm
        'Conflict Minerals (CMRT)',  // score_cmrt
        'EU REACH - Candidate List & SCIP',  // score_reach
        'REACH Authorisation List', // score_reach_auth
        'EU RoHS',  // score_rohs
        'EU REACH Authorisation and Restriction' // score_reach_auth_v2
    ];
    private readonly _allColumns = [
        BrowserStateModels.ColumnId.Photo, 
        BrowserStateModels.ColumnId.SupplierName, 
        BrowserStateModels.ColumnId.Name, 
        BrowserStateModels.ColumnId.Category, 
        BrowserStateModels.ColumnId.Fmd, 
        BrowserStateModels.ColumnId.Sds
    ];

    // ===============================================================================

    private getDynRegsIds() {
        const regulationIds: string[] = []

        for (var dynReg of Object.values(this.props.dynamicRegulationTable)) {
            if (!dynReg.isStandardRegulation) {
                continue;
            }

            regulationIds.push(dynReg.regulationId);
        }

        return regulationIds;
    }

    private readonly _filterMenuIdsToTruncate: string[] = this.getDynRegsIds();

    // ===============================================================================

    private createfilterMenuIdsToTruncateFilterMenu() {
        const filterMenuIdsToTruncateFilter: { [filterMenuId: string]: boolean } = {}

        for (let filterId of this._filterMenuIdsToTruncate) {
            filterMenuIdsToTruncateFilter[filterId] = true;
        }

        return filterMenuIdsToTruncateFilter;
    }

    // ===============================================================================

    public componentDidMount() {
        if (!this.props.companyId) {
            this.getSharedMaterials(0, this.props.searchMode);
        }
    }

    // ===============================================================================

    public componentWillUnmount() {
       
    }

    // ===============================================================================

    private getDynRegsAsTableColumns() {
        const columns: BrowserStateModels.Column[] = []

        for (var dynReg of Object.values(this.props.dynamicRegulationTable)) {
            if (!dynReg.isStandardRegulation) {
                continue;
            }

            columns.push(
                {
                    name: dynReg.config.shortName,
                    columnId: dynReg.regulationId,
                    width: 60
                }
            );
        }

        return columns;
    }

    // ===============================================================================

    private getNonRegulationSharedMaterialColumns() {
        const columns: BrowserStateModels.Column[] = [
            {
                name: "",
                columnId: BrowserStateModels.ColumnId.Checkbox,
                width: 25
            },
            {
                name: "",
                columnId: BrowserStateModels.ColumnId.Photo,
                width: 75
            },
            {
                name: this.localizeString("Company"),
                columnId: BrowserStateModels.ColumnId.SupplierName,
                width: 200
            },
            {
                name: this.localizeString("Name"),
                columnId: BrowserStateModels.ColumnId.Name,
                width: 200
            },
            {
                name: this.localizeString("Category"),
                columnId: BrowserStateModels.ColumnId.Category,
                width: 150
            },
            {
                name: this.localizeString("Disclosure Level"),
                columnId: BrowserStateModels.ColumnId.Fmd,
                width: 150
            },
            {
                name: "SDS",
                columnId: BrowserStateModels.ColumnId.Sds,
                width: 60
            },
        ];

        return columns.toDictionary(c => c.columnId);
    }

    // ===============================================================================

    public render() {
        return (
            <div id="sm2-browser-content">
                <div id="sm2-browser-sidebar">
                    {this.renderFilterSelectionSection()}
                </div>
                <div id="sm2-browser-search-shared-materials-section">
                    <div id="search-and-sort-container">
                        {this.renderSearchContainer()}
                        {this.renderSortDropdown()}
                    </div>
                    {this.renderAllActiveFilters()}
                    {this.renderTotalNumberOfResults()}
                    {this.renderHomeMaterialTable()}
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderSortDropdown() {
        // empty strings are columns that should not be able to be sorted by
        const sortTypeToFriendlyDescription = [this.localizeString("Company"), this.localizeString("Passport Name"), this.localizeString("Category"), this.localizeString("Featured")];

        const ascendingOrDescending = this.state.sortDirection === SortDirectionType.Ascending ? this.localizeString("A to Z") : this.localizeString("Z to A");
        const toggleSortDescription = this.state.sortType === BrowserStateModels.SortTypes.Featured ? this.localizeString("Featured") : sortTypeToFriendlyDescription[this.state.sortType] + ": " + ascendingOrDescending;

        // const for clarity in function calls
        const asc = SortDirectionType.Ascending;
        const desc = SortDirectionType.Descending;

        return (
            <div className="btn-group" id="sort-dropdown">
                <button type="button" className="btn btn-default btn-sm dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">{this.localizeString("Sort")}: {toggleSortDescription} <span className="caret"></span></button>
                <ul className="dropdown-menu dropdown-menu-right">
                    {(this.props.browserEndpoint !== SharedMaterialsDatabaseStateController.BrowserEndpoint.Supplier) && (this.props.searchMode !== BrowserStateModels.BrowserSearchMode.Companies) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.Featured, asc)}>{this.localizeString("Featured")}</a></li>}
                    {(this.props.browserEndpoint !== SharedMaterialsDatabaseStateController.BrowserEndpoint.Supplier) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.SupplierName, asc)}>{this.localizeString("Company: A to Z")}</a></li>}
                    {(this.props.browserEndpoint !== SharedMaterialsDatabaseStateController.BrowserEndpoint.Supplier) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.SupplierName, desc)}>{this.localizeString("Company: Z to A")}</a></li>}
                    {(this.props.searchMode !== BrowserStateModels.BrowserSearchMode.Companies) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.PassportName, asc)}>{this.localizeString("Passport Name: A to Z")}</a></li>}
                    {(this.props.searchMode !== BrowserStateModels.BrowserSearchMode.Companies) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.PassportName, desc)}>{this.localizeString("Passport Name: Z to A")}</a></li>}
                    {(this.props.searchMode !== BrowserStateModels.BrowserSearchMode.Companies) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.Category, asc)}>{this.localizeString("Category: A to Z")}</a></li>}
                    {(this.props.searchMode !== BrowserStateModels.BrowserSearchMode.Companies) && <li><a role="button" onClick={e => this.sortDropdownItem_click(BrowserStateModels.SortTypes.Category, desc)}>{this.localizeString("Category: Z to A")}</a></li>}
                </ul>
            </div>
        );
    }

    // ===============================================================================

    private renderHomeMaterialTable() {
        if (this.props.isLoadingCatalog) {
            return (
                <div className="tn-loading-message">
                    <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                </div>
            );
        }
        return (
            <div className="sm2-material-table-container">
                <div className="sm2-material-table-scroll-wrapper">
                    <div className="sm2-material-table">
                        <ToxnotReactTable
                            headerRow={this.getTableColumns()}
                            allRowModels={this.props.sharedMaterialsCoreData}
                            getRowForTable={(data: SharedMaterialDataModelType, rowIndex) => this.getRowTableData(data, rowIndex) as TableRow}
                            enablePaging={true}
                            localizer={this.props.appServices}
                            rowClickEventHandler={(e, _sharedMaterial: SharedMaterialDataModelType) => {
                                const sharedMaterial = _sharedMaterial as SharedMaterialCoreData;
                                sharedMaterial.hasOwnProperty('sharedMaterialId') ? this.materialRow_click(sharedMaterial.sharedMaterialId) : null;
                            }}
                            selectingRowOptions={this.props.browserEndpoint === SharedMaterialsDatabaseStateController.BrowserEndpoint.Home
                                ? undefined : {
                                    onRowSelected: (sharedMaterialIds) => {
                                        console.log("sharedMaterialIds", sharedMaterialIds);
                                        this.props.selectSharedMaterial(sharedMaterialIds);
                                    },
                                    rowModelGetId: (sharedMaterial: any) => { return sharedMaterial.sharedMaterialId },
                                    initiallySelected: this.props.currentlySelectedSharedMaterialIds,
                                }}
                            onPageChange={(newPage: number) => this.handlePageChange(newPage)}
                            rowsPerPage={this._RowPerPageValue}
                            totalNoResult={this.props.totalNumberOfCatalog}
                            currentPage={this.props.currentPage}
                            noResultFoundContent={this.renderRequestNewSharedMaterial()}
                        />
                    </div>
                </div>
            </div>
        );
    }



    // ===============================================================================

    private getSharedMaterials(newPage: number, searchMode = BrowserStateModels.BrowserSearchMode.Materials) {
        const companyId = this.props.companyId ? this.props.companyId : null;
        
        if (searchMode !== BrowserStateModels.BrowserSearchMode.Companies) {
            this.props.loadSharedMaterialsIfNeeded(
                this.props.appServices,
                this.getQueryParameter("accessCode"),
                newPage,
                this.state.searchValue,
                this._RowPerPageValue,
                this.state.selectedFilters,
                this.state.sortType,
                this.state.sortDirection,
                companyId
            );
        } else {
            this.props.getAllSharedMaterialsCompaniesData(
                newPage,
                this._RowPerPageValue,
                this.state.searchValue,
                this.state.sortDirection
            );
        }
    }

    // ===============================================================================

    private handlePageChange(newPage: number) {
        this.getSharedMaterials(newPage, this.props.searchMode);
    }

    // ===============================================================================

    private getTableColumns() {
        const _sharedMaterialsTableColumns = this.getNonRegulationSharedMaterialColumns();
        const _regulationColumns = this.getDynRegsAsTableColumns();

        const homeColumns = this._allColumns;
        const supplierColumns = this._allColumns.filter(c => c !== "SupplierName");

        const regulationColumns = _regulationColumns.map((colItem) => {
            return {
                value: colItem.name
            }
        });

        const columns = this.props.browserEndpoint === SharedMaterialsDatabaseStateController.BrowserEndpoint.Home
            ? homeColumns : supplierColumns;

        const sharedMaterialsTableColumns = columns.map((colItem) => {
            return {
                value: _sharedMaterialsTableColumns[colItem].name
            }
        });

        return [...sharedMaterialsTableColumns, ...regulationColumns];
    }

    // ===============================================================================

    private getRestrictedMessage() {
        const count = this.getTableColumns().length;
        return {
            content: (
                <td colSpan={count - 4} className="restricted-data-message">
                    {this.localizeString("Select the material to request access to its data.")}
                </td>
            )
        };
    }

   // ===============================================================================

    private getRowTableData(sharedMaterial: SharedMaterialDataModelType, rowIndex: number) {
        if (sharedMaterial.hasOwnProperty('sharedMaterialId')) {
            return this.getSharedMaterialRowData(sharedMaterial as SharedMaterialCoreData, rowIndex);
        } else {
            return this.getSupplierRowData(sharedMaterial as SharedMaterialsCatalogCompany);
        }
    }
   
    // ===============================================================================

    private getSharedMaterialRowData(sharedMaterial: SharedMaterialCoreData, rowIndex: number) {
        const sharedMaterialsTableColumns = this.getNonRegulationSharedMaterialColumns();
        
        const regulationColumns = this.getDynRegsAsTableColumns().map(r =>
            this.renderHomeMaterialTableRowComplianceCell(
                SharedMaterialsReactUtilities.getComplianceIconForComplianceDetermination(sharedMaterial.regulationIdToComplianceScoreMap[r.columnId]),
                r.width,
                r.columnId
            )
        );

        const materialHomePageInitialColumns = this.props.browserEndpoint === SharedMaterialsDatabaseStateController.BrowserEndpoint.Home ? [
            '',
            this.renderHomeMaterialTableRowCell(sharedMaterial.companyName.emptyCoalesce("Unknown"), 100),
        ] : [''];

        const cols: (string | JSX.Element | JSX.Element[] | { content: JSX.Element })[] = [
            ...materialHomePageInitialColumns,
            this.renderHomeMaterialTableRowCell(sharedMaterial.materialName, 100),
            this.renderHomeMaterialTableRowCell(this.createCommaSeperatedListOfCategories(sharedMaterial.categories).coalesceToMaxSizeAndIndicateTruncationWithEllipsis(25), 100),
            this.renderHomeMaterialTableRowCell(sharedMaterial.fmdStatus !== null ? localizeSharedMaterialFmdDisclosureType(sharedMaterial.fmdStatus, this.props.appServices) : "", sharedMaterialsTableColumns[BrowserStateModels.ColumnId.Fmd].width),
            this.renderHomeMaterialTableRowCell(sharedMaterial.hasSdsStatus === true ? this.localizeString("Yes") : this.localizeString("No"), sharedMaterialsTableColumns[BrowserStateModels.ColumnId.Sds].width),
        ];

        if (sharedMaterial.accessLevel !== SharedMaterialAccessType.Public && !sharedMaterial.hasAccess) {
            cols.push(this.getRestrictedMessage());
        } else {
            cols.push(...regulationColumns);
        }

        return { cols };
    }

    // ===============================================================================

    private getSupplierRowData(supplier: SharedMaterialsCatalogCompany) {
        const sharedMaterialsTableColumns = this.getNonRegulationSharedMaterialColumns();
        const companyLogoFileId: string | null = supplier?.logoFileId;

        return {
            cols: [
                this.renderHomeMaterialTableRowCell(companyLogoFileId !== null ? `/Api/GeneralFileImage?fileId=${encodeURIComponent(companyLogoFileId)}&overrideTeamOwnerId=${encodeURIComponent(supplier.ownerId)}` : null, sharedMaterialsTableColumns[BrowserStateModels.ColumnId.Photo].width, true),
                this.renderHomeMaterialTableRowCell(supplier.name.emptyCoalesce("Unknown"), sharedMaterialsTableColumns[BrowserStateModels.ColumnId.SupplierName].width),
                this.renderHomeMaterialTableRowCell(
                    <Link to={`${ToxnotReactComponentPaths.SharedMaterialSupplierBase}/${supplier.ownerId}/${supplier.companyId}`} role="button" onClick={e => this.viewCompanyPageLink_click()}>
                        {this.localizeString("View Company Page")}
                    </Link>,
                    sharedMaterialsTableColumns[BrowserStateModels.ColumnId.SupplierName].width
                ),
                this.renderHomeMaterialTableRowCell(
                    <Link to={`${ToxnotReactComponentPaths.ContactSupplier}/${supplier.companyId}`} role="button">
                        {this.localizeString("Contact")}
                    </Link>,
                    sharedMaterialsTableColumns[BrowserStateModels.ColumnId.Category].width
                )
            ].concat(Array(8).fill(""))
        };
    }

    // ===============================================================================

    private renderRequestNewSharedMaterial() {
        return (
            <div className="sm2-request-new-shared-material-section">
                {this.localizeString("Nothing matched your search criteria.")}
                {this.props.browserEndpoint === SharedMaterialsDatabaseStateController.BrowserEndpoint.Home &&
                    <React.Fragment>{" "}<span>{this.localizeString("Please search again or")} <Link to={ToxnotReactComponentPaths.GetStartedRequestData} onClick={e => this.requestNewSharedMaterialLink_click()} id="no-results-found-link">{this.localizeString("Request a New Material Passport")}</Link></span></React.Fragment>
                }
            </div>
        );
    }

    // ===============================================================================

    private renderHomeMaterialTableRowCell(value: string | JSX.Element | null, width: number, isPhoto: boolean = false) {
        const maxDim = Math.floor(width * .75);
        return (
            <div className="sm2-table-col" style={{ width: width }}>
                {isPhoto
                    ? (value !== null ? (<img src={value as string} style={{ maxHeight: maxDim, maxWidth: maxDim }} />) : null)
                    : value}
            </div >
        );
    }

    // ===============================================================================

    private renderHomeMaterialTableRowComplianceCell(complianceIcon: JSX.Element, width: number, regulationId: string) {
        return (
            <div key={regulationId} className="sm2-table-col" style={{ width: width }}>
                {complianceIcon}
            </div >
        );
    }

    // ===============================================================================

    private renderSearchContainer() {
        return (
            <div id="search-container">
                <div className="input-group">
                    <input id="search-text-input" 
                        onKeyDown={this.searchInput_keyDown} 
                        type="text" 
                        className="form-control" 
                        onChange={(e) => this.onSearchInputChange(e)}
                        placeholder={this.props.searchMode === BrowserStateModels.BrowserSearchMode.Companies ?
                            this.localizeString("Search by Company") :
                            this.localizeString("Search by Company, Material Name, or SKU...")
                        } 
                    />
                    <span className="input-group-btn">
                        <button id="initiate-search-button" className="btn btn-default" type="button" onClick={e => this.addSearchFilter()}><span className="glyphicon glyphicon-search" aria-hidden="true"></span></button>
                    </span>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderTotalNumberOfResults() {
        if (this.props.activeFilters.length === 0) {
            return null;
        }

        const numSelectedItems = Object.keys(this.props.currentlySelectedSharedMaterialIds).length;

        return (
            <div id="current-materials-statuses">
                <div id="total-number-of-results-container">
                    <p>
                        {this.props.filteredSharedMaterialsCoreData.length} results
                    </p>
                </div>
                {numSelectedItems > 0 && (
                    <div id="total-number-of-selected-container">
                        <p>
                            {Object.keys(this.props.currentlySelectedSharedMaterialIds).length} selected
                        </p>
                    </div>
                )}
            </div>

        );
    }

    // ===============================================================================

    private renderFilterSelectionSection() {
        return (
            <div className="sm2-browser-filters">
                {this.props.browserEndpoint === SharedMaterialsDatabaseStateController.BrowserEndpoint.Home && this.renderSearchModeOptions()}

                {this.props.filterMenus.map(filterMenu => (
                    <CollapsibleContainer key={`${filterMenu.filterId}${(filterMenu.filterParameter ?? "")}`} opened={filterMenu.expanded} onClick={() => this.toggleFilterMenu(filterMenu.filterId)} title={filterMenu.title}>
                        {this.renderOpenFilterMenu(filterMenu)}
                    </CollapsibleContainer>)
                )}
            </div>
        );
    }

    // ===============================================================================

    private renderSearchModeOptions() {
        return (
            <div id="search-mode-container" className="filter-menu-container">
                <div className="filter-menu-check-box-container search-mode-option" onClick={e => this.searchModeRadioButton_onClick(BrowserStateModels.BrowserSearchMode.Materials)}>
                    <input onChange={e => () => { return; }} type="radio" value={BrowserStateModels.BrowserSearchMode.Materials} name="search-mode" checked={this.props.searchMode === BrowserStateModels.BrowserSearchMode.Materials} /> {this.localizeString("Passports")}
                </div>
                <div className="filter-menu-check-box-container search-mode-option" onClick={e => this.searchModeRadioButton_onClick(BrowserStateModels.BrowserSearchMode.Companies)}>
                    <input onChange={e => () => { return; }} type="radio" value={BrowserStateModels.BrowserSearchMode.Companies} name="search-mode" checked={this.props.searchMode === BrowserStateModels.BrowserSearchMode.Companies} /> {this.localizeString("Companies")}
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderAllActiveFilters() {
        const activeFiltersToRender = this.props.activeFilters.filter(filter => filter.filterId !== FilterId.SupplierId);

        if (activeFiltersToRender.length === 0) {
            return null;
        }

        return (
            <div className="sm2-browser-active-filters active-filters-container">
                <div className="active-filter-values-container">
                    {activeFiltersToRender.map(filter => (
                        <div key={filter.filterId + filter.value} className="active-filter-value">
                            <span>{filter.filterId !== FilterId.SharedMaterialsSearch ? this.props.filterMenus.filter(f => f.filterId === filter.filterId)[0]?.title + ": " + this.getFilterValueToFriendlyName(filter.value) : this.getFilterValueToFriendlyName(filter.value)}</span>
                            {" "}
                            <span className="remove-filter-value-button" onClick={e => this.removeFilter_click(filter.filterId, filter.value)}>x</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderOpenFilterMenu(filterMenu: BrowserStateModels.FilterMenu) {
        let truncateVal = this.state.filterMenuIdsToTruncateFilterMenu[filterMenu.filterId] ? this._filterMenuDefaultOptionTruncateVal : filterMenu.values.length;

        const body =
            filterMenu
                .values
                .take(truncateVal)
                .map((value, index) => (
                    <div key={index} className="checkbox">
                        <label>
                            <input
                                type="checkbox"
                                onChange={e => this.filterCheckbox_onChange(e, filterMenu.filterId, value)}
                                checked={this.isFilterMenuItemChecked(filterMenu.filterId, value)}
                                disabled={this.props.searchMode === BrowserStateModels.BrowserSearchMode.Companies}
                            />
                            {this.getFilterValueToFriendlyName(value)}
                        </label>
                    </div>
                ));

        if (truncateVal < filterMenu.values.length) {
            body.push(<a key={filterMenu.values.length} role="button" onClick={e => this.changeFilterMenuViewOptionNum_click(filterMenu.filterId, false)}>{this.localizeStringFormat("View {0} more", filterMenu.values.length - truncateVal)}</a>)
        }
        else if ((truncateVal === filterMenu.values.length) && (truncateVal > this._filterMenuDefaultOptionTruncateVal)) {
            body.push(<a key={filterMenu.values.length} role="button" onClick={e => this.changeFilterMenuViewOptionNum_click(filterMenu.filterId, true)}>{this.localizeString("View less")}</a>)
        }

        return (
            <div className="filter-menu-check-box-container">
                {body}
            </div>
        );
    }
    // ===============================================================================

    private onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;

        this.setState({ searchValue: value }, () => {
            if (value === '') {
                this.getSharedMaterials(0, this.props.searchMode);
            }
        });
    }

    // ===============================================================================

    private filterCheckbox_onChange = (e: React.ChangeEvent<HTMLInputElement>, filterId: string, value: string) => {
        const checked = e.currentTarget.checked;

        // Define the specific filters and their mapping
        const valueMapping: { [key: string]: string } = {
            'Compliant': "1",
            'PossiblyComplaint': "0"
        };

        this.setState(prevState => {
            const selectedFilters = { ...prevState.selectedFilters };

            // Initialize the selected filter array if it doesn't exist
            if (!selectedFilters[filterId]) {
                selectedFilters[filterId] = [];
            }

            // Apply the specific mapping if the filterId matches
            if (this.legacyComplianceFilters.includes(filterId)) {
                const mappedValue = valueMapping[value];
                if (checked) {
                    if (!selectedFilters[filterId].includes(mappedValue)) {
                        selectedFilters[filterId].push(mappedValue);
                    }
                } else {
                    selectedFilters[filterId] = selectedFilters[filterId].filter(v => v !== mappedValue);
                }
            } else {
                // Default behavior for other filters
                if (checked) {
                    if (!selectedFilters[filterId].includes(value)) {
                        selectedFilters[filterId].push(value);
                    }
                } else {
                    selectedFilters[filterId] = selectedFilters[filterId].filter(v => v !== value);
                }
            }

            return { selectedFilters };
        }, () => {
            this.getSharedMaterials(0);
        });
    }

    private isFilterMenuItemChecked(filterId: string, value: string) {
        const valueMapping: { [key: string]: string } = {
            'Compliant': "1",
            'PossiblyComplaint': "0"
        };

        if (this.legacyComplianceFilters.includes(filterId)) {
            const mappedValue = valueMapping[value];
            return this.state.selectedFilters[filterId] && this.state.selectedFilters[filterId].includes(mappedValue);
        }

        return this.state.selectedFilters[filterId] && this.state.selectedFilters[filterId].includes(value);
    }

    private addSearchFilter() {
        this.props.appServices.trackEvent("exchange-browser-searched");

        if (window.location.pathname.startsWith(ToxnotReactComponentPaths.SharedMaterialsHomeStartPath)) {
            this.props.appServices.trackEvent("get-started-get-supply-chain-data-exchange-browser-searched");
        }

        if (this.state.searchValue === '') {
            return;
        }

        this.getSharedMaterials(0, this.props.searchMode);
    }

    // ===============================================================================

    private createCommaSeperatedListOfCategories(categories: string[]) {
        return categories.join(", ");
    }

    // ===============================================================================

    private getFilterValueToFriendlyName(value: string) {
        const filterValueToFriendlyName: Record<string, string> = {
            [BrowserStateModels.RegulationFiterType.PossiblyCompliant]: this.localizeString("Possibly Compliant"),
            [BrowserStateModels.RegulationFiterType.Compliant]: this.localizeString("Compliant")
        }

        const result = filterValueToFriendlyName[value];

        if (!result) {
            return value;
        }

        return result;
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private toggleFilterMenu = (filterId: string) => {
        this.props.toggleFilterMenu(this.props.browserEndpoint, filterId);
    }

    // ===============================================================================

    private removeFilter_click = (filterId: string, filterValue: string) => {
        this.props.deactivateFilter(this.props.browserEndpoint, filterId, filterValue);
    }

    // ===============================================================================

    private searchInput_keyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            this.addSearchFilter();
        }
    }

    // ===============================================================================

    private sortDropdownItem_click = (sortType: BrowserStateModels.SortTypes, ascending: SortDirectionType) => {
        this.props.appServices.trackEvent("exchange-browser-sorted");

        // Update state first
        this.setState({
            sortType: sortType,
            sortDirection: ascending
        }, () => {
            this.getSharedMaterials(0, this.props.searchMode);
        });
    }

    // ===============================================================================

    private requestNewSharedMaterialLink_click = () => {
        this.props.appServices.trackEvent("exchange-requested-new-passport");
        this.props.initializeRequest();
    }

    // ===============================================================================

    private viewCompanyPageLink_click = () => {
        if (window.location.pathname.startsWith(ToxnotReactComponentPaths.SharedMaterialsHomeStartPath)) {
            this.props.appServices.trackEvent("get-started-get-supply-chain-data-exchange-viewed-company-page");
        }
    }

    // ===============================================================================

    private materialRow_click = (sharedMaterialId: string) => {
        if (window.location.pathname.startsWith(ToxnotReactComponentPaths.SharedMaterialsHomeStartPath)) {
            this.props.appServices.trackEvent("get-started-get-supply-chain-data-exchange-viewed-passport");
        }

        this.props.history.push(ToxnotReactComponentPaths.SharedMaterialBaseDetailPath + `/${sharedMaterialId}`);
    }

    // ===============================================================================

    private searchModeRadioButton_onClick = (searchMode: BrowserStateModels.BrowserSearchMode) => {
        // Browser Endpoint is assumed to be Home.
        this.props.setSearchMode(searchMode);
        this.getSharedMaterials(0, searchMode);
    }

    // ===============================================================================

    private changeFilterMenuViewOptionNum_click = (filterMenuId: string, truncate: boolean) => {
        const newFilterMenuIdsToTruncateFilterMenu = { ...this.state.filterMenuIdsToTruncateFilterMenu };
        newFilterMenuIdsToTruncateFilterMenu[filterMenuId] = truncate;

        this.setState({
            filterMenuIdsToTruncateFilterMenu: newFilterMenuIdsToTruncateFilterMenu
        });
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState, ownProps: OwnProps) => {
        const browserState = ownProps.browserEndpoint === SharedMaterialsDatabaseStateController.BrowserEndpoint.Home
            ? state.sharedMaterialsState?.homeBrowserState
            : state.sharedMaterialsState?.supplierBrowserState;

        return {
            ...state.sharedMaterialsState,
            ...browserState,
            appServices: state.appServices
        };
    },
    SharedMaterialsDatabaseStateController.actionCreators // Selects which action creators are merged into the component's props
)(withRouter(SharedMaterialsBrowser) as any);
